import {
  Bell,
  X,
  Podcast,
  Heart,
  Sparkles,
  Trash2,
  Check,
  ChevronRight,
} from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

import { Notification, NotificationType } from "../hooks/useFollowedPodcasts";

interface Props {
  notifications: Notification[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onRemoveNotification?: (id: string) => void;
  onNotificationClick?: (podcastId: string) => void;
}

const NotificationDropdown: React.FC<Props> = ({
  notifications,
  unreadCount,
  onMarkAllRead,
  onClearAll,
  onRemoveNotification,
  onNotificationClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exitingId, setExitingId] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 12,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      setTimeout(() => onMarkAllRead(), 500);
    }
  };

  const handleRemove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setExitingId(id);
    setTimeout(() => {
      onRemoveNotification?.(id);
      setExitingId(null);
    }, 300);
  };

  const [now, setNow] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (timestamp: number) => {
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    if (seconds > 10) return `${seconds}s`;
    return "now";
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case "follow":
        return <Heart size={14} className="text-pink-400" />;
      case "new_episode":
        return <Podcast size={14} className="text-indigo-400" />;
      case "welcome":
        return <Sparkles size={14} className="text-yellow-400" />;
      case "milestone":
        return <Sparkles size={14} className="text-radio-accent" />;
      default:
        return <Bell size={14} className="text-white/50" />;
    }
  };

  const getGradient = (type: NotificationType) => {
    switch (type) {
      case "follow":
        return "from-pink-500/20 to-rose-500/5";
      case "new_episode":
        return "from-indigo-500/20 to-purple-500/5";
      case "welcome":
        return "from-yellow-500/20 to-orange-500/5";
      case "milestone":
        return "from-cyan-500/20 to-blue-500/5";
      default:
        return "from-white/10 to-white/5";
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300
                    ${
                      isOpen
                        ? "bg-radio-accent/20 text-radio-accent shadow-[0_0_20px_rgba(34,211,238,0.3)]"
                        : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white/10"
                    }
                `}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} new)` : ""}`}
      >
        <Bell
          size={18}
          className={`transition-transform ${isOpen ? "scale-110" : ""}`}
        />

        {/* Premium Badge with Glow */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-gradient-to-br from-pink-500 to-rose-600 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-[0_0_12px_rgba(236,72,153,0.6)] animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Portal */}
      {isOpen &&
        dropdownPos &&
        ReactDOM.createPortal(
          <div
            className="fixed w-[280px] sm:w-[340px] max-h-[75vh] overflow-hidden rounded-3xl z-[9999] animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300"
            style={{
              top: dropdownPos.top,
              right: dropdownPos.right,
              background:
                "linear-gradient(135deg, rgba(20,20,30,0.98) 0%, rgba(10,10,15,0.99) 100%)",
              backdropFilter: "blur(40px) saturate(180%)",
              boxShadow:
                "0 25px 80px -12px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.1)",
            }}
          >
            {/* Header with Gradient Line */}
            <div className="relative p-4 border-b border-white/5">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-radio-accent/50 to-transparent" />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-radio-accent/20 to-indigo-500/20 flex items-center justify-center">
                    <Bell size={16} className="text-radio-accent" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">
                      Notifications
                    </h3>
                    {notifications.length > 0 && (
                      <p className="text-[10px] text-white/40">
                        {notifications.length} aktiviteter
                      </p>
                    )}
                  </div>
                </div>

                {notifications.length > 0 && (
                  <button
                    onClick={onClearAll}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium text-white/50 hover:text-white hover:bg-white/10 transition-all group"
                  >
                    <Trash2
                      size={12}
                      className="group-hover:text-red-400 transition-colors"
                    />
                    Rensa
                  </button>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] flex items-center justify-center">
                    <Bell size={28} className="text-white/20" />
                  </div>
                  <p className="text-sm font-medium text-white/60">
                    Inga notifikationer
                  </p>
                  <p className="text-xs text-white/30 mt-1 max-w-[200px] mx-auto">
                    Följ podcasts för att få uppdateringar om nya avsnitt!
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {notifications.map((notif, idx) => (
                    <div
                      key={notif.id}
                      onClick={() =>
                        notif.podcastId &&
                        onNotificationClick?.(notif.podcastId)
                      }
                      className={`group relative p-3 rounded-2xl cursor-pointer transition-all duration-300
                                            ${
                                              exitingId === notif.id
                                                ? "opacity-0 translate-x-4 scale-95"
                                                : "opacity-100 translate-x-0 scale-100"
                                            }
                                            ${!notif.read ? "bg-gradient-to-r " + getGradient(notif.type) : "hover:bg-white/5"}
                                        `}
                      style={{
                        animationDelay: `${idx * 50}ms`,
                      }}
                    >
                      {/* Unread Indicator Glow */}
                      {!notif.read && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-radio-accent shadow-[0_0_8px_rgba(34,211,238,0.6)]" />
                      )}

                      <div className="flex items-start gap-3">
                        {/* Icon/Logo */}
                        <div className="relative flex-shrink-0">
                          {notif.logoUrl ? (
                            <img
                              src={notif.logoUrl}
                              alt=""
                              className="w-11 h-11 rounded-xl object-cover ring-1 ring-white/10"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center ring-1 ring-white/10">
                              <Podcast size={20} className="text-white/70" />
                            </div>
                          )}
                          {/* Type Badge */}
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-lg bg-black/80 backdrop-blur flex items-center justify-center ring-1 ring-white/10">
                            {getNotificationIcon(notif.type)}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-white/80 truncate">
                                {notif.title}
                              </p>
                              <p className="text-sm text-white truncate mt-0.5">
                                {notif.message}
                              </p>
                            </div>
                            <span className="text-[10px] text-white/30 flex-shrink-0">
                              {formatTime(notif.timestamp)}
                            </span>
                          </div>

                          {/* Action hint */}
                          {notif.podcastId && (
                            <div className="flex items-center gap-1 mt-2 text-[10px] text-radio-accent/70 opacity-0 group-hover:opacity-100 transition-opacity">
                              <span>Visa podcast</span>
                              <ChevronRight size={10} />
                            </div>
                          )}
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={(e) => handleRemove(e, notif.id)}
                          className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center transition-all flex-shrink-0"
                        >
                          <X
                            size={12}
                            className="text-white/40 hover:text-red-400"
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-white/5 bg-black/20">
                <button
                  onClick={onMarkAllRead}
                  className="w-full py-2 rounded-xl text-xs font-medium text-white/50 hover:text-white hover:bg-white/5 transition-all flex items-center justify-center gap-2"
                >
                  <Check size={12} />
                  Markera alla som lästa
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default NotificationDropdown;
